import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, crumbLd } from "@/components/Breadcrumbs";
import { SeoLinks } from "@/components/SeoLinks";
import { Loader2, MapPin } from "lucide-react";

const CAT = {
  "agricultural-land": { type: "agricultural", label: "Agricultural Land" },
  "farm-land": { type: "agricultural", label: "Farm Land" },
  plots: { type: "plot", label: "Plots" },
  "open-plots": { type: "plot", label: "Open Plots" },
  houses: { type: "homes", label: "Houses" },
  homes: { type: "homes", label: "Homes" },
  commercial: { type: "commercial", label: "Commercial Properties" },
};

export default function CategoryLocationPage() {
  const { category, location } = useParams();
  const cat = CAT[category];

  const { data: locations = [], isLoading: locLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const loc = locations.find((l) => l.slug === location);
  const locName = loc ? loc.name : location.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const params = { property_type: cat?.type };
  if (loc) params[loc.type === "city" ? "city" : "locality"] = loc.name;
  else params.q = locName;

  const { data = [], isLoading } = useQuery({
    queryKey: ["cat-loc", category, location, cat?.type],
    queryFn: async () => (await api.get("/properties", { params })).data,
    enabled: !!cat,
  });

  if (!cat) {
    return (
      <div className="px-4 max-w-3xl mx-auto py-24 text-center">
        <p className="text-slate-500">Category not found.</p>
        <Link to="/properties" className="text-emerald-700 font-semibold mt-3 inline-block">Browse all properties</Link>
      </div>
    );
  }

  const crumbs = [{ name: "Home", to: "/" }, { name: cat.label, to: `/properties` }, { name: locName }];

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="cat-loc-page">
      <Seo
        title={`${cat.label} for Sale in ${locName}, Telangana | Dotlands Realty`}
        description={`Explore verified ${cat.label.toLowerCase()} for sale in ${locName}, Telangana. Browse Dotlands Realty listings with locations, sizes and prices in ${locName}.`}
        path={`/properties/${category}/${location}`}
        jsonLd={crumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />
      <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-[#0E3B2E] flex items-center gap-2">
        <MapPin className="w-6 h-6 text-[#C8A24C]" /> {cat.label} for Sale in {locName}
      </h1>
      <p className="text-sm text-slate-500 mt-4 mb-4" data-testid="cat-loc-count">
        {isLoading || locLoading ? "Loading..." : `${data.length} ${data.length === 1 ? "property" : "properties"} found`}
      </p>

      {isLoading || locLoading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No active {cat.label.toLowerCase()} listings in {locName} right now. <Link to="/properties" className="text-emerald-700 font-semibold">See all properties</Link></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      )}

      <SeoLinks />
    </div>
  );
}
